import Application from "../../Application";
import { Constants } from "../../core/common/Constants";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import ShowWarningMessageInteractor from "../../core/interactor/notifications/ShowWarningMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import CreateIndexTypeAttributesInteractor from "../interactors/CreateIndexTypeAttributesInteractor";
import CreateIndexTypeInteractor from "../interactors/CreateIndexTypeInteractor";
import DeleteIndexTypeAttributesInteractor from "../interactors/DeleteIndexTypeAttributesInteractor";
import DeleteIndexTypeInteractor from "../interactors/DeleteIndexTypeInteractor";
import GetAllIndexTypeAttributesInteractor from "../interactors/GetAllIndexTypeAttributesInteractor";
import GetAllIndexTypesInteractor from "../interactors/GetAllIndexTypesInteractor";
import GetIndexAttributeDataByTypeInteractor from "../interactors/GetIndexAttributeDataByTypeInteractor";
import UpdateIndexTypeInteractor from "../interactors/UpdateIndexTypeInteractor";
import { IIndexType } from "../model/IndexType";
import { IIndexTypeAttribute } from "../model/IndexTypeAttribute";
import IndexTypeAttributeMapper from "../service/IndexTypeAttributeMapper";

export interface TIndexTypePresenter extends TLoadingAwarePresenter {
  indexTypes: IIndexType[];
  createModalVisible: boolean;
  indexTypeAttributes?: IIndexTypeAttribute[];
  newIndexTypeAttributes?: IIndexTypeAttribute[];
}
export interface IIndexTypePresenter extends TIndexTypePresenter, TPresentable {
  loadAllIndexTypes(indexTypes: IIndexType[]): void;
  createIndexType(indexTypes: IIndexType): Promise<boolean>;
  updateIndexType(indexTypes: IIndexType): void;
  deleteIndexType(indexTypes: IIndexType): void;
  onStartUpdateIndexType(e: any): void;
  createIndexTypeAttribute(
    indexTypes: IIndexType,
    attribute: string,
    required: boolean
  ): void;
  deleteIndexTypeAttribute(indexTypeAttribute: IIndexTypeAttribute): void;
}
const defaultState: TIndexTypePresenter = {
  indexTypes: [],
  createModalVisible: false,
  indexTypeAttributes: [],
  newIndexTypeAttributes: []
};
const IndexTypePresenter = withStore<IIndexTypePresenter, TIndexTypePresenter>(
  ({ application, store, loader, translate }): IIndexTypePresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TIndexTypePresenter>();

    loader.start("indexTypeLoader");
    const loadAllIndexTypes = (indexTypes: IIndexType[]) => {
      _store.update({
        indexTypes
      });
      loader.stop("indexTypeLoader");
    };
    const getAllIndexTypes = async () => {
      let indexTypes = await _application.container
        .resolve<GetAllIndexTypesInteractor>("getAllIndexTypes")
        .execute();
      loadAllIndexTypes(indexTypes);
    };

    const createIndexType = async (indexType: IIndexType) => {
      loader.start("indexTypeLoader");
      await _application.container
        .resolve<CreateIndexTypeInteractor>("createIndexType")
        .execute(indexType);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.index_type.create_success_message"));
      loader.stop("indexTypeLoader");
      await getAllIndexTypes();
      return true;
    };

    const updateIndexType = async (indexType: IIndexType) => {
      loader.start("indexTypeLoader");
      const { newIndexTypeAttributes } = _store.getState<TIndexTypePresenter>();
      await _application.container
        .resolve<UpdateIndexTypeInteractor>("updateIndexType")
        .execute(indexType);
      newIndexTypeAttributes &&
        (await _application.container
          .resolve<CreateIndexTypeAttributesInteractor>(
            "createIndexTypeAttributes"
          )
          .execute(newIndexTypeAttributes));
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.index_type.create_success_message"));
      loader.stop("indexTypeLoader");
      await getAllIndexTypes();
    };

    const deleteIndexType = async (indexType: IIndexType) => {
      loader.start("indexTypeLoader");
      await _application.container
        .resolve<DeleteIndexTypeInteractor>("deleteIndexType")
        .execute(indexType);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.index_type.create_success_message"));
      loader.stop("indexTypeLoader");
      await getAllIndexTypes();
    };

    const onStartUpdateIndexType = async (indexType: IIndexType) => {
      loader.start("indexTypeLoader");
      let indexTypeAttributes = await _application.container
        .resolve<GetAllIndexTypeAttributesInteractor>(
          "getAllIndexTypeAttributes"
        )
        .execute(indexType.id);
      _store.update({
        indexTypeAttributes
      });
      loader.stop("indexTypeLoader");
    };

    const createIndexTypeAttribute = async (
      indexType: IIndexType,
      attribute: string,
      required: boolean
    ) => {
      loader.start("indexTypeLoader");
      const { indexTypeAttributes, newIndexTypeAttributes } =
        _store.getState<TIndexTypePresenter>();
      if (
        indexTypeAttributes?.find(
          (a) => a.attribute === attribute && a.required == required
        )
      ) {
        await _application.container
          .resolve<ShowWarningMessageInteractor>("showWarningMessage")
          .execute(translate("editors.index_types.entry_already_exists"));
        loader.stop("indexTypeLoader");
        return;
      }
      let indexTypeAttribute = {
        indexTypeId: indexType.id,
        attribute: attribute,
        required: required ? Constants.YES : Constants.NO,
        valid: Constants.YES
      };
      indexTypeAttributes?.push(IndexTypeAttributeMapper(indexTypeAttribute));
      newIndexTypeAttributes?.push(
        IndexTypeAttributeMapper(indexTypeAttribute)
      );
      _store.update({
        indexTypeAttributes,
        newIndexTypeAttributes
      });
      loader.stop("indexTypeLoader");
    };

    const deleteIndexTypeAttribute = async (
      indexTypeAttribute: IIndexTypeAttribute
    ) => {
      loader.start("indexTypeLoader");
      const { indexTypeAttributes, newIndexTypeAttributes } =
        _store.getState<TIndexTypePresenter>();

      var existingAttributeData = await _application.container
        .resolve<GetIndexAttributeDataByTypeInteractor>(
          "getIndexAttributeDataByIndex"
        )
        .execute(indexTypeAttribute?.indexTypeId);
      if (existingAttributeData.length > 0) {
        await _application.container
          .resolve<ShowWarningMessageInteractor>("showWarningMessage")
          .execute(translate("editors.index_types.entry_already_exists"));
        loader.stop("indexTypeLoader");
        return;
      }
      indexTypeAttribute?.id &&
        (await _application.container
          .resolve<DeleteIndexTypeAttributesInteractor>(
            "deleteIndexTypeAttributes"
          )
          .execute(indexTypeAttribute?.id));
      let filteredIndexTypeAttributes = indexTypeAttributes?.filter(
        (a) => a.attribute !== indexTypeAttribute?.attribute
      );
      let filteredNewIndexTypeAttributes = newIndexTypeAttributes?.filter(
        (a) => a.attribute !== indexTypeAttribute?.attribute
      );
      _store.update({
        indexTypeAttributes: filteredIndexTypeAttributes,
        newIndexTypeAttributes: filteredNewIndexTypeAttributes
      });
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.index_type.create_success_message"));
      loader.stop("indexTypeLoader");
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadAllIndexTypes,
      createIndexType,
      updateIndexType,
      deleteIndexType,
      onStartUpdateIndexType,
      createIndexTypeAttribute,
      deleteIndexTypeAttribute
    };
  },
  defaultState
);
export default IndexTypePresenter;
